import React, { useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Excel from 'exceljs';
import axios from 'axios';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import ExcelDropzone from '../Inputs/ExcelDropzone';
import TableBody from '@mui/material/TableBody';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { cmmUtils } from 'utils/cmmUtils.js';
import Backdrop from '@mui/material/Backdrop';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { LinearProgress } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import wait from 'waait';

const moment = require('moment');

// ---------- 유틸 ----------
const numberWithCommas = (x) =>
  Number(x || 0)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const sanitize = (s = '') =>
  s
    .replace(/\(.+?\)\s*/g, '') // 괄호 및 내부 제거
    .replace(/[^\w가-힣·\-\s.]/g, '') // 허용 외 특수문자 제거
    .replace(/\s+/g, ' ')
    .trim();

const headTwoTokens = (s = '') => s.split(' ').slice(0, 2).join('').replace(/\s/g, '');

const getFileName = (contentDisposition = '') => {
  try {
    // filename*=UTF-8''... or filename="..."
    const m1 = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(contentDisposition);
    const m2 = /filename=([^;]+)/i.exec(contentDisposition);
    const raw = (m1?.[1] || m2?.[1] || '').replace(/"/g, '').trim();
    return raw ? decodeURIComponent(raw) : null;
  } catch {
    return null;
  }
};

const readCsvSmart = (file) =>
  new Promise((resolve) => {
    const fr = new FileReader();
    fr.onload = () => {
      const buf = new Uint8Array(fr.result);
      const hasBOM = buf[0] === 0xef && buf[1] === 0xbb && buf[2] === 0xbf;
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsText(file, hasBOM ? 'UTF-8' : 'EUC-KR');
    };
    fr.readAsArrayBuffer(file);
  });

// ---------- 진행바 ----------
const LinearProgressWithLabel = (props) => {
  const total = props.totalCount || 0;
  const done = (props.successCount || 0) + (props.errorCount || 0);
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <Box sx={{ zIndex: 5000 }}>
      <Card variant="outlined" sx={{ width: 500, height: 145, backgroundColor: 'white' }}>
        <CardContent sx={{ pb: 0, pt: 2 }}>
          <Typography sx={{ fontSize: 14 }} gutterBottom>
            변환 중 (총 {total} / 성공 {props.successCount} / 실패 {props.errorCount})
            <br />
            시작 시간 : {props.nowDate} (예상 소요시간 - 약 {numberWithCommas(Math.ceil(total > 60 ? total / 60 : total))}
            {total > 60 ? '분' : '초'})
          </Typography>
          <Divider />
          <Box display="flex" alignItems="center" sx={{ mt: 1 }}>
            <Box sx={{ width: '90%', ml: 1, mr: 1 }}>
              <LinearProgress variant="determinate" value={pct} />
            </Box>
            <Box minWidth={35}>
              <Typography variant="body2" color="textSecondary">{`${pct}%`}</Typography>
            </Box>
          </Box>
          <Divider sx={{ mb: 1 }} />
          <Button variant="contained" color="error" onClick={props.convertCancel} fullWidth>
            중지
          </Button>
        </CardContent>
      </Card>

      {props.errorList.length > 0 && (
        <Card variant="outlined" sx={{ width: 500, backgroundColor: 'white', zIndex: 3500, mt: 1 }}>
          <CardContent sx={{ pb: 0, pt: 2 }}>
            <Typography sx={{ fontSize: 14 }} gutterBottom>
              실패 목록({props.errorList.length}건)
            </Typography>
            <Divider />
            <TableContainer component={'div'} sx={{ overflowY: 'scroll', maxHeight: 300 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 300 }} align={'center'}>
                      주소
                    </TableCell>
                    <TableCell align={'center'}>실패 사유</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {props.errorList.map((row, rowIdx) => (
                    <TableRow hover key={`err-${rowIdx}`}>
                      <TableCell>{row.address}</TableCell>
                      <TableCell>{row.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

// ---------- 메인 컴포넌트 ----------
export default function GeoCoding(props) {
  const { open, handleClose, getDataList } = props;

  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState([]);
  const [dataColumns, setDataColumns] = useState([]);
  const [dataList, setDataList] = useState([]);
  const [addressColumn, setAddressColumn] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [backdropOpen, setBackdropOpen] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [errorList, setErrorList] = useState([]);
  const convertFlag = useRef(true);
  const [nowDate, setNowDate] = useState('');
  const [busy, setBusy] = useState(false); // 변환/중지 중 버튼 잠금

  const handleDropFile = async (acceptedFiles) => {
    setDataColumns([]);
    setDataList([]);
    setFile(null);

    const f = acceptedFiles?.[0];
    if (!f) return;

    if (f.type === 'text/csv') {
      const text = await readCsvSmart(f);
      const csvJson = cmmUtils.csvToJSON(text);
      setDataColumns(text.split('\r\n')[0].split(','));
      setDataList(csvJson);
      setFile(acceptedFiles);
    } else {
      let tempDataList = [];
      let tempColumns = [];
      const workbook = new Excel.Workbook();
      const reader = new FileReader();
      reader.readAsArrayBuffer(f);
      reader.onload = () => {
        const buffer = reader.result;
        workbook.xlsx.load(buffer).then((wb) => {
          wb.eachSheet((sheet) => {
            // 1-based index
            sheet.eachRow((row, rowIndex) => {
              if (rowIndex === 1) {
                for (let i = 1; i < row.values.length; i++) {
                  tempColumns.push(row.values[i]);
                }
              } else {
                let dataInfo = {};
                for (let i = 1; i < row.values.length; i++) {
                  dataInfo[tempColumns[i - 1]] = row.values[i] !== undefined ? row.values[i] : null;
                }
                tempDataList.push(dataInfo);
              }
            });
          });
          setDataColumns(tempColumns);
          setDataList(tempDataList);
          setFile(acceptedFiles);
        });
      };
    }
  };

  const handleChangeSelect = (e) => setAddressColumn(e.target.value);

  const convertGeoCoding = async () => {
    try {
      if (dataList.length <= 0) {
        setErrorMessage('파일을 먼저 선택해주세요.');
        return;
      }
      if (!addressColumn && addressColumn !== 0) {
        setErrorMessage('주소 필드를 선택해주세요.');
        return;
      }
      setErrorMessage('');

      convertFlag.current = true;
      setNowDate(moment(new Date()).format('YYYY-MM-DD HH:mm:ss'));
      setBackdropOpen(true);
      setBusy(true);

      // 주소 필드 주입
      const addrKey = dataColumns[addressColumn];
      const addressList = dataList.map((row) => ({
        ...row,
        address: row[addrKey]
      }));

      const result = await getGeoCodeList(addressList);

      // GeoJSON Feature 생성
      const features = [];
      const tmpErrors = [];
      result.forEach((r) => {
        if (r.status === 'OK' && r.point) {
          features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [Number(r.point.x), Number(r.point.y)] },
            properties: r
          });
        } else {
          tmpErrors.push({ address: r.address, status: r.status });
        }
      });

      const data = {
        type: 'geo',
        features,
        fileName: `${file?.[0]?.path?.substring(0, file?.[0]?.path?.lastIndexOf('.')) || 'geocoding_result'}.zip`,
        errorList: tmpErrors,
        dataColumns,
        errorCount: tmpErrors.length
      };

      await convertShp(data);
    } catch (e) {
      alert('변환 실패!');
      // eslint-disable-next-line no-console
      console.log(e);
    } finally {
      setBackdropOpen(false);
      setBusy(false);
    }
  };

  // 주소 리스트 변환(직렬 + 502 재시도)
  const getGeoCodeList = async (addressList) => {
    let tmpSuccessCount = 0;
    let tmpErrorCount = 0;
    const resultAddressList = [];
    const tmpErrorList = [];
    let error502List = [];

    // 1차 루프
    for (let i = 0; i < addressList.length; i++) {
      if (!convertFlag.current) break;
      const address = addressList[i];
      await wait(1000); // 기본 텀
      const result = await convertGeoCode(address.address);
      address.point = result?.point;
      address.status = result?.status;

      if (result?.status === 'OK') {
        tmpSuccessCount += 1;
        resultAddressList.push(address);
      } else if (String(result?.status).includes('502')) {
        error502List.push(address);
      } else {
        tmpErrorCount += 1;
        tmpErrorList.push(address);
        resultAddressList.push(address);
      }

      setSuccessCount(tmpSuccessCount);
      setErrorList(tmpErrorList);
      setErrorCount(tmpErrorCount);
    }

    // 502 재시도 루프
    while (error502List.length > 0 && convertFlag.current) {
      const snapshot = error502List.slice();
      error502List = [];

      for (const address of snapshot) {
        if (!convertFlag.current) break;
        await wait(1000);
        const result = await convertGeoCode(address.address);
        address.point = result?.point;
        address.status = result?.status;

        if (result?.status === 'OK') {
          tmpSuccessCount += 1;
          resultAddressList.push(address);
        } else if (String(result?.status).includes('502')) {
          error502List.push(address); // 다시 시도
        } else {
          tmpErrorCount += 1;
          tmpErrorList.push(address);
          resultAddressList.push(address);
        }

        setSuccessCount(tmpSuccessCount);
        setErrorList(tmpErrorList);
        setErrorCount(tmpErrorCount);
      }
    }

    return resultAddressList;
  };

  const convertGeoCode = async (rawAddress) => {
    try {
      const address = sanitize(String(rawAddress || ''));
      if (!address) return { address: rawAddress, status: 'INVALID_ADDRESS' };

      const refined = await refineAddress(address);
      if (refined.status !== 'OK') return { address: rawAddress, status: refined.status };

      const j = refined.juso;

      const res = await axios.get(process.env.REACT_APP_JUSO_COORD_API_URL, {
        responseType: 'text',
        params: {
          confmKey: process.env.REACT_APP_JUSO_COORD_CONFM_KEY,
          admCd: j.admCd,
          rnMgtSn: j.rnMgtSn,
          udrtYn: j.udrtYn,
          buldMnnm: j.buldMnnm,
          buldSlno: j.buldSlno,
          resultType: 'json'
        }
      });

      const data = res.data;
      const common = data?.results?.common;
      const coord = data?.results?.juso?.[0];

      if (!common) return { address: rawAddress, status: 'INVALID_RESPONSE' };
      if (common.errorCode !== '0') return { address: rawAddress, status: `${common.errorCode} ${common.errorMessage}` };
      if (!coord) return { address: rawAddress, status: 'NOT_FOUND' };

      const x = coord.entX ?? coord.posX ?? coord.x;
      const y = coord.entY ?? coord.posY ?? coord.y;

      return {
        address: rawAddress,
        status: 'OK',
        point: { x: Number(x), y: Number(y) },
        roadAddr: j.roadAddr,
        jibunAddr: j.jibunAddr
      };
    } catch (e) {
      return { address: rawAddress, status: e?.message || 'ERROR' };
    }
  };

  const refineAddress = async (raw) => {
    const q = sanitize(raw);
    if (!q) return { address: raw, status: 'INVALID_ADDRESS' };

    try {
      const res = await axios.get(process.env.REACT_APP_JUSO_ADDR_API_URL, {
        responseType: 'text',
        params: {
          confmKey: process.env.REACT_APP_JUSO_ADDR_CONFM_KEY,
          currentPage: 1,
          countPerPage: 1,
          keyword: q,
          resultType: 'json'
        }
      });

      const data = res.data;
      const common = data?.results?.common;
      const juso = data?.results?.juso?.[0];

      if (!common) return { address: raw, status: 'INVALID_RESPONSE' };
      if (common.errorCode !== '0') return { address: raw, status: `${common.errorCode} ${common.errorMessage}` };
      if (!juso) return { address: raw, status: 'NOT_FOUND' };

      // 기존 검증 로직(앞 두 토큰 포함) 유지하고 싶으면 여기서 한번 체크
      const headTwo = headTwoTokens(q);
      const candidate = sanitize(juso.roadAddr || juso.jibunAddr || '');
      if (!candidate.replace(/\s/g, '').includes(headTwo)) return { address: raw, status: 'NOT_FOUND' };

      return { status: 'OK', juso };
    } catch (e) {
      return { address: raw, status: e?.message || 'ERROR' };
    }
  };

  const convertShp = async (data) => {
    if (data.features.length > 0) {
      try {
        const response = await axios.post(`/api/geo-code/convert`, data, { responseType: 'blob' });
        const contentType = response.headers['content-type'] || 'application/zip';
        const blob = new Blob([response.data], { type: contentType });

        const cd = response.headers['content-disposition'] || '';
        let name = getFileName(decodeURIComponent(cd)) || data.fileName || 'geocoding_result.zip';

        if (window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob, name);
        } else {
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.target = '_self';
          link.download = name;
          link.click();
          window.URL.revokeObjectURL(link);
        }

        alert(`성공 : ${data.features.length}건\r\n실패 : ${data.errorList.length}건`);
        await handleClose();
        await getDataList();
      } catch (error) {
        alert('변환 실패!');
        // eslint-disable-next-line no-console
        console.log(error);
      }
    } else {
      alert(`성공 : ${data.features.length}건\r\n실패 : ${data.errorList.length}건`);
      await handleClose();
      await getDataList();
    }
  };

  const convertCancel = async () => {
    if (window.confirm('중지 후 재시작 할 수 없습니다. 진행하시겠습니까?')) {
      convertFlag.current = false;
      setBackdropOpen(false);
      setBusy(false);
    }
  };

  useEffect(() => {
    const tempColumns = dataColumns.map((dc) => ({ id: dc, label: dc, align: 'center' }));
    setColumns(tempColumns);
  }, [dataColumns]);

  useEffect(() => {
    if (open) {
      setFile(null);
      setDataColumns([]);
      setColumns([]);
      setDataList([]);
      setAddressColumn(null);
      setErrorMessage(null);
      setSuccessCount(0);
      setErrorList([]);
      setErrorCount(0);
      setBackdropOpen(false);
      convertFlag.current = true;
      setNowDate('');
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onClose={busy ? undefined : handleClose} aria-labelledby="draggable-dialog-title" fullWidth maxWidth="xl" sx={{ '& .MuiDialog-paper': { width: 600, maxWidth: '95vw' } }}>
      <Box component="form" noValidate sx={{ mt: 1, minWidth: 300 }}>
        <DialogTitle id="draggable-dialog-title">지오코딩 (주소 → 좌표)</DialogTitle>
        <DialogContent dividers>
          <Box>
            <TableContainer component={'div'}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ width: 130 }}>주소 필드</TableCell>
                    <TableCell>
                      {dataColumns.length > 0 && (
                        <FormControl sx={{ minWidth: 300 }}>
                          <InputLabel id="label">주소 필드</InputLabel>
                          <Select labelId="label" id="addressColumn" name="addressColumn" value={addressColumn} label="주소 필드" onChange={handleChangeSelect} fullWidth size="small">
                            {dataColumns.map((item, idx) => (
                              <MenuItem key={`${idx}`} value={idx}>
                                <ListItemText primary={`${item}`} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>건수</TableCell>
                    <TableCell>
                      {dataList.length > 0 && `${numberWithCommas(dataList.length)} (예상 소요시간 - 약 ${numberWithCommas(Math.ceil(dataList.length > 60 ? dataList.length / 60 : dataList.length))}${dataList.length > 60 ? '분' : '초'})`}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* 미리보기 */}
          <Box sx={{ mt: 3 }}>
            <TableContainer component={'div'}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell key={column.id} align={column.align} sx={{ minWidth: column.minWidth }}>
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dataList.slice(0, 5).map((row, rowIdx) => (
                    <TableRow hover key={`preview-${rowIdx}`}>
                      {columns.map((col, colIdx) => {
                        const v = row[col.id];
                        const isDateLike = v && typeof v === 'object' && moment(v).isValid();
                        return (
                          <TableCell key={`row${rowIdx}-col${colIdx}`} align={col.align}>
                            {isDateLike ? moment(v).format('YYYY-MM-DD') : v}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                  {dataList.length > 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} align={'center'}>
                        ⠇
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow hover />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* 업로드 */}
          <Box sx={{ mt: 3 }}>
            <ExcelDropzone
              accept={{
                'text/csv': ['.csv'],
                'application/vnd.ms-excel': ['.xls'],
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
              }}
              onAcceptedFiles={handleDropFile}
              id="panorama"
              maxFiles={1}
              maxFilesize={5242880}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <span style={{ color: 'red', fontSize: 'small', margin: '10px 10px 0px 10px', flexGrow: 1 }}>{errorMessage}</span>

          <span style={{ fontSize: '12px', color: '#666', marginRight: '8px' }}>※ 결과는 EPSG:5179 좌표계로 저장됩니다</span>

          <Button variant="contained" color="primary" onClick={convertGeoCoding} disabled={busy}>
            변환
          </Button>
          <Button variant="outlined" color="success" onClick={handleClose} disabled={busy}>
            닫기
          </Button>
        </DialogActions>
      </Box>

      <Backdrop open={backdropOpen} sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
        <LinearProgressWithLabel totalCount={dataList.length} successCount={successCount} errorCount={errorCount} convertCancel={convertCancel} errorList={errorList} nowDate={nowDate} />
      </Backdrop>
    </Dialog>
  );
}
